import { useEffect, useRef } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { ApiError, setUnauthorizedHandler } from './api.js';
import {
  ACCOUNT_PATH,
  FORGOT_PATH,
  LOGIN_PATH,
  INVITE_PATH,
  ONBOARDING_PATH,
  REGISTER_PATH,
  RESET_PATH,
  VERIFY_PATH,
  shouldRedirectToLogin,
} from './lib/auth.js';
import AuthGate from './components/AuthGate.js';
import Layout from './components/Layout.js';
import EmptyState from './components/EmptyState.js';
import AcceptInvite from './pages/AcceptInvite.js';
import Billing, { BILLING_PATH } from './pages/Billing.js';
import Login from './pages/Login.js';
import MockCheckout, { MOCK_CHECKOUT_PATH } from './pages/MockCheckout.js';
import Onboarding from './pages/Onboarding.js';
import Hesap from './pages/Hesap.js';
import { ParolaSifirlaTalep, ParolaYenile } from './pages/ParolaSifirla.js';
import Register from './pages/Register.js';
import VerifyEmail from './pages/VerifyEmail.js';
import Funnel from './pages/Funnel.js';
import Goals from './pages/Goals.js';
import Deneyler from './pages/Deneyler.js';
import DeneyDetay from './pages/DeneyDetay.js';
import Heatmap from './pages/Heatmap.js';
import Insights from './pages/Insights.js';
import Overview from './pages/Overview.js';
import Seo from './pages/Seo.js';
import Sessions from './pages/Sessions.js';
import Organizasyon, { ORG_PATH } from './pages/Organizasyon.js';
import Settings from './pages/Settings.js';
import UzunVadeli from './pages/UzunVadeli.js';
import Webhooks, { WEBHOOKS_PATH } from './pages/Webhooks.js';
import Kilavuz from './pages/Kilavuz.js';
import Retention from './pages/Retention.js';

/**
 * §13 — Rota haritası. Türkçe yollar:
 *   '/'          Genel Bakış  (§13.1 — 1. sayfa; §13.2 notuna rağmen yönlendirme YOK)
 *   '/bulgular'  Bulgular     (§13.2)
 *   '/funnel'    Funnel       (§13.3)
 *   '/hedefler'  Hedefler     (§13.4)
 *   '/oturumlar' Oturumlar    (§13.5)
 *   '/heatmap'   Isı Haritası (§23.5 — yol şartnamede İNGİLİZCE geçer, Türkçeleştirilmez)
 *   '/seo'       SEO          (§24.7 — aynı gerekçe: yol şartnamede '/seo'dur)
 *   '/uzun-vadeli' Uzun Vadeli (§19.3 — HAZIR yönetici raporu; canlı ağır sorgu çalışmaz)
 *   '/ayarlar'   Ayarlar      (§21.3 Settings.tsx; §26.6 e-posta abonelikleri + §27.7 Üyeler)
 *   '/organizasyon'      Organizasyon (§42.5 org üyeleri + §46.5 kurumsal giriş)
 *   '/faturalama'        Faturalama (§45 — org planı, abonelik, fatura geçmişi)
 *   '/webhooks'          Webhook'lar (§47 — ORG düzeyi; yol şartname terimiyle İNGİLİZCE)
 *   '/odeme-simulasyonu' Mock ödeme (§45.1 — GUARD İÇİ; adresi sunucu üretir)
 *   '/giris'     Giriş        (§27.7 — GUARD DIŞI)
 *   '/davet'     Davet kabul  (§27.7 — GUARD DIŞI; §27.4 `BASE_URL/davet?token=…`)
 *   '/kayit'     Kayıt        (§43.2 — GUARD DIŞI; bayrak kapalıysa sayfa "kapalı" der)
 *   '/dogrula'   Doğrulama    (§43.2 — GUARD DIŞI; `BASE_URL/dogrula?token=…`)
 *   '/parola-sifirla' Sıfırlama talebi (§73.3 — GUARD DIŞI)
 *   '/parola-yenile'  Yeni parola      (§73.3 — GUARD DIŞI; `?token=…`)
 *   '/hesap'     Hesap        (§73.1 — GUARD İÇİ; avatar menüsünden açılır)
 *   '/kurulum'   Sihirbaz     (§43.2/4 — GUARD İÇİ: org kurmak oturum ister)
 *
 * §27.7'nin "kayıt sayfası yoktur" kararı §43 ile DEĞİŞTİ: kayıt sayfası vardır ama
 * `SELF_SERVE_SIGNUP` varsayılan olarak KAPALIDIR (§43.1). §72.5/7 ile bir kez daha
 * güncellendi: giriş sayfası bayrağı kök `/health`ten okur ve bağlantıyı YALNIZ
 * açıkken çizer — kapalı kurulumda hâlâ ölü bir link yoktur, açık kurulumda ise
 * kullanıcı kayda ulaşabilir.
 *
 * Sunucu durumu react-query ile yönetilir; sayfalar `src/api.ts` fonksiyonlarını
 * `queryFn` olarak kullanır.
 */

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Panel operasyonel bir araç: sekmeye dönünce taze veri iyi, ama gürültü olmasın.
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      // Yetki hatasında (401/403) tekrar denemek anlamsız — token yanlış.
      retry: (failureCount, error) => {
        if (error instanceof ApiError && (error.isAuth || error.status === 404)) return false;
        return failureCount < 2;
      },
    },
    mutations: { retry: false },
  },
});

/** Bilinmeyen yol — §13'in beş sayfası + §23.5 ısı haritası + §24.7 SEO + §21.3 Ayarlar dışında bir şey yok. */
function NotFound() {
  /*
   * §72.5/3 — `actionHref` (yani `<a href>`) SPA'da TAM SAYFA YÜKLEMESİ yapardı:
   * React ağacı, query önbelleği ve oturum durumu sıfırlanır, kullanıcı bir "sayfa
   * bulunamadı"dan çıkmanın bedeli olarak tüm paneli yeniden indirirdi. Router'ın
   * kendi gezinmesi kullanılır. `actionHref` prop'u KALIR — dış bağlantılar (kurulum
   * dokümanı) için doğru araç odur.
   */
  const navigate = useNavigate();
  return (
    <EmptyState
      icon="?"
      title="Sayfa bulunamadı"
      description="Bu adres panelde tanımlı değil. Sol menüden bir sayfa seçin."
      actionLabel="Bulgular'a git"
      onAction={() => navigate('/bulgular')}
    />
  );
}

/**
 * §27.7 — "Herhangi bir API yanıtı 401 gelirse aynı yönlendirme (oturum süresi dolumu)".
 *
 * `api.ts` router'ı tanımaz; 401'i yalnız HABER VERİR. Bu köprü bileşeni kancayı
 * router'ın içinden kurar. Konum bir `ref`'te tutulur: kancayı her gezinmede yeniden
 * kurmak (effect bağımlılığına `location` koymak) devam eden isteklerin kancasını
 * ortada bırakırdı; ref ile kanca bir kez kurulur ve her zaman GÜNCEL yolu okur.
 */
function UnauthorizedRedirect() {
  const navigate = useNavigate();
  const location = useLocation();
  const locationRef = useRef(location);
  locationRef.current = location;

  useEffect(() => {
    setUnauthorizedHandler(() => {
      const current = locationRef.current;
      // `/giris` ve `/davet`'te 401 normaldir (form hatası) — yönlendirme YAPILMAZ.
      if (!shouldRedirectToLogin(401, current.pathname)) return;
      navigate(LOGIN_PATH, {
        replace: true,
        state: { from: `${current.pathname}${current.search}` },
      });
    });
    return () => setUnauthorizedHandler(null);
  }, [navigate]);

  return null;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <UnauthorizedRedirect />
        <Routes>
          {/*
            §27.7 — oturum EDİNME sayfaları guard'ın DIŞINDA: içeride olsalardı
            `me` 401 → /giris → guard → `me` 401 … sonsuz yönlendirme olurdu.
          */}
          <Route path={LOGIN_PATH} element={<Login />} />
          <Route path={INVITE_PATH} element={<AcceptInvite />} />
          {/* §43.2 — kayıt ve doğrulama da oturum EDİNME sayfalarıdır (aynı gerekçe). */}
          <Route path={REGISTER_PATH} element={<Register />} />
          <Route path={VERIFY_PATH} element={<VerifyEmail />} />
          {/*
            §73.3 — parola sıfırlama da OTURUM EDİNME akışıdır: parolasını unutan
            kullanıcının tanım gereği oturumu yoktur, guard'ın arkasında bu iki sayfa
            hiç açılamazdı (login/davet ile birebir aynı gerekçe).
          */}
          <Route path={FORGOT_PATH} element={<ParolaSifirlaTalep />} />
          <Route path={RESET_PATH} element={<ParolaYenile />} />

          <Route element={<AuthGate />}>
            <Route element={<Layout />}>
              <Route index element={<Overview />} />
              {/*
                §43.2/4 — sihirbaz kabuğun İÇİNDEDİR: org'suz kullanıcıyı buraya
                `Layout` yönlendirir ve org kurulana kadar başka sayfaya bırakmaz.
              */}
              <Route path={ONBOARDING_PATH.slice(1)} element={<Onboarding />} />
              <Route path="bulgular" element={<Insights />} />
              <Route path="funnel" element={<Funnel />} />
              <Route path="hedefler" element={<Goals />} />
              <Route path="deneyler" element={<Deneyler />} />
              {/* §102.2 — deneyin kendi adresi: paylaşılabilir/yer imlenebilir detay. */}
              <Route path="deneyler/:id" element={<DeneyDetay />} />
              <Route path="oturumlar" element={<Sessions />} />
              <Route path="heatmap" element={<Heatmap />} />
              <Route path="seo" element={<Seo />} />
              <Route path="uzun-vadeli" element={<UzunVadeli />} />
              {/* §93 — retention kohortları; Uzun Vadeli ile aynı "dönemsel" halka. */}
              <Route path="retention" element={<Retention />} />
              <Route path="ayarlar" element={<Settings />} />
              {/* Panel içi kullanma kılavuzu — statik içerik, uca istek atmaz. */}
              <Route path="kilavuz" element={<Kilavuz />} />
              {/* §73.1 — hesap sayfası; avatar menüsünden açılır, rayda YERİ YOKTUR
                  (ray ÜRÜN sayfalarınındır, kullanıcının kendi kaydının değil). */}
              <Route path={ACCOUNT_PATH.slice(1)} element={<Hesap />} />
              {/* §42.5/§46.5 — ORG düzeyi: üyeler + kurumsal giriş. */}
              <Route path={ORG_PATH.slice(1)} element={<Organizasyon />} />
              {/* §45 — faturalama ORG düzeyindedir; sayfa aktif org'u bağlamdan okur. */}
              <Route path={BILLING_PATH.slice(1)} element={<Billing />} />
              {/* §47 — webhook'lar da ORG düzeyi (`webhooks.org_id`); aynı bağlam. */}
              <Route path={WEBHOOKS_PATH.slice(1)} element={<Webhooks />} />
              {/*
                §45.1 — mock ödeme sayfası GUARD'IN İÇİNDE: adresini sunucu üretir
                (`createCheckout`) ve akış oturumu olan bir kullanıcıyla başlar. Guard
                dışına alsaydık, oturumsuz açılan bir bağlantı ödemeyi "tamamlayıp"
                kullanıcıyı girişe atardı — hangi org'a ne olduğu belirsiz kalırdı.
              */}
              <Route path={MOCK_CHECKOUT_PATH.slice(1)} element={<MockCheckout />} />
              <Route path="*" element={<NotFound />} />
            </Route>
          </Route>
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
